import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '@shared/components/ui';
import { DocumentEntity } from '@core/entities';
import { HumanizePipe, TruncateTextPipe } from '@shared/pipes';
import { TippyModule } from '@ngneat/helipopper';
import { TranslateModule } from '@ngx-translate/core';
import { DocumentCategory, HeadlineType } from '@core/enums';
import { PermissionService } from '@auth';
import { GetHeadlineOption, GetKeycloakId } from '@core/utils';

@Component({
  selector: 'app-document-card',
  imports: [CommonModule, IconComponent, HumanizePipe, TruncateTextPipe, TippyModule, TranslateModule],
  templateUrl: './document-card.component.html',
  styleUrls: ['./document-card.component.scss'],
})
export class DocumentCardComponent {
  @Input() document: DocumentEntity;
  @Input() isDownloadingState = false;
  @Input() showMobileViewCard = false;
  @Input() isLoadingPreview = false;
  @Output() moveToFolder: EventEmitter<{ entity: DocumentEntity; folder: DocumentCategory }> = new EventEmitter<{ entity: DocumentEntity; folder: DocumentCategory }>();
  @Output() delete: EventEmitter<DocumentEntity> = new EventEmitter<DocumentEntity>();
  @Output() download: EventEmitter<DocumentEntity> = new EventEmitter<DocumentEntity>();
  @Output() previewImage: EventEmitter<DocumentEntity> = new EventEmitter<DocumentEntity>();
  // documentNamePaths: string[] = ['/application-vnd.openxmlformats-officedocument.spreadsheetml.sheet/', '/application-pdf/', '/text-plain/', '/image-jpeg/', '/image-png/'];
  protected readonly DocumentCategory = DocumentCategory;
  private readonly _permissionService = inject(PermissionService);

  get canDelete() {
    if (this.document.category === DocumentCategory.PERSONAL) {
      return true;
    } else if (this.document.category === DocumentCategory.CLUBS || this.document.category === DocumentCategory.CURRENT_STATUSES || this.document.category === DocumentCategory.ARCHIVE) {
      return this._permissionService.isAdmin();
    } else {
      return this.document.createdBy === GetKeycloakId() || this._permissionService.isAdmin() || (!this._permissionService.isGuest() && !this._permissionService.isMember());
    }
  }

  get isHeadlineEnterprise() {
    return GetHeadlineOption() === HeadlineType.ENTERPRISE;
  }

  get canMove() {
    return this._permissionService.isAdmin() || this._permissionService.isFunctionary();
  }

  get isAdmin() {
    return this._permissionService.isAdmin();
  }

  get documentNameWithoutExtension() {
    return this.document?.docName.split('/').pop().split('.').shift() || 'No Name';
  }

  get documentName() {
    return this.document?.docName.split('/').pop() || 'No Name';
  }

  get documentType() {
    // Supported types:
    // sketch, folder, zip, psd, ppt, pptx, png, jpg, jpeg, ai, pdf, docx, doc, txt, xls, xlsx

    const fileExtension = this.document?.docName.split('.').pop()?.toLowerCase();

    const extensionMap: Record<string, string> = {
      pdf: 'pdf',
      docx: 'word',
      doc: 'word',
      xlsx: 'excel',
      xls: 'excel',
      ppt: 'ppt',
      pptx: 'ppt',
      png: 'image',
      jpg: 'image',
      jpeg: 'image',
      gif: 'image',
      psd: 'psd',
      ai: 'ai',
      sketch: 'sketch',
      zip: 'zip',
      txt: 'txt',
      folder: 'folder',
    };

    return extensionMap[fileExtension] || 'file';
  }

  moveFileTo(folderCategory: DocumentCategory) {
    if (!this.canMove) return;
    this.moveToFolder.emit({ entity: this.document, folder: folderCategory });
  }

  downloadFile() {
    this.download.emit(this.document);
  }

  deleteFile() {
    if (!this.canDelete) return;
    this.delete.emit(this.document);
  }

  previewImageFile() {
    if (this.documentType === 'image' && !this.isLoadingPreview) {
      this.previewImage.emit(this.document);
    }
  }
}
